import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Swal from 'sweetalert2';
import { addCommas } from '../../utils/functions/commas';
import { getStorage } from '../../utils/functions/localstorage';
import { useUpdateStatementOnlineMutation } from '../../services/statementOnline';

dayjs.extend(utc);

const formatStatementDateTime = (value) =>
  value ? dayjs.utc(value).add(7, 'hour').format('YYYY-MM-DD HH:mm:ss.SSS') : '-';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getStatementAmount = (...values) => {
  const amounts = values.map(toNumber);
  return Math.abs(amounts.find((amount) => amount !== 0) || 0);
};

const STATUS_MATCH = 'Match';
const STATUS_UNMATCH = 'Unmatch';
const STATUS_PARTIAL = 'Partial';
const DETAIL_PARTIAL_SUSPENSE = 'Partial Suspense';

const style = document.createElement('style');
style.innerHTML = `
.container-class {
  z-index: 999999 !important;
}
`;
document.head.appendChild(style);
export default function StatementOnlineEditDialog(props) {
  const {
    t,
    onClose,
    value: valueProp,
    open,
    statusItems,
    detailStatusItems,
    refetch,
    ...other
  } = props;

  const [value, setValue] = useState(valueProp);
  const [newStatus, setNewStatus] = useState('');
  const [newdetailStatus, setNewDetailStatus] = useState('');
  const [newRemark, setnewRemark] = useState('');
  const [newspendAmount, setnewSpendAmount] = useState(0);
  const userName = getStorage('name');
  const [updateStatement, responseUpdateStatement] =
    useUpdateStatementOnlineMutation();
  const statementAmount = getStatementAmount(
    valueProp?.amount,
    valueProp?.credit_amount,
    valueProp?.debit_amount,
  );
  const currentSpendAmount = toNumber(
    newspendAmount ?? valueProp?.new_spend_amount ?? valueProp?.spend_amount,
  );
  const currentBalance = statementAmount
    ? statementAmount - currentSpendAmount
    : toNumber(valueProp?.new_balance ?? valueProp?.balance);

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    }
  }, [valueProp, open]);

  const handleCancel = () => {
    onClose();
  };

  const handleOk = async () => {
    try {
      if (currentSpendAmount < 0 || currentSpendAmount > statementAmount) {
        throw new Error(
          `ยอดใช้ไปต้องอยู่ระหว่าง 0 ถึง ${addCommas(statementAmount)}`,
        );
      }

      const payload = {
        params: {
          // eslint-disable-next-line no-underscore-dangle
          id: valueProp._id,
        },
        body: {
          spendAmount: currentSpendAmount,
          status: newStatus,
          detailStatus: newdetailStatus || '',
          remark: newRemark || '',
          balance: currentBalance,
          editBy: userName,
        },
      };
      const res = await updateStatement(payload);
      if (res.error) {
        throw res.error;
      }

      switch (res.data.status) {
        case 'success':
          onClose();
          Swal.fire({
            title: t('app.statusUpdatedSuccessfully'), // ✅ แก้ไข
            icon: 'success',
            showConfirmButton: false,
            timer: 3000,
            willOpen: () => {
              document.querySelector('.swal2-container').style.zIndex = '2000'; // ปรับค่า z-index ที่ต้องการ
            },
          }).then(() => {
            refetch();
          });
          break;

        default:
          break;
      }
      // console.log('res', res);
      // console.log('payload', payload);
    } catch (error) {
      console.log(error);
      const errorMessage =
        error?.data?.error?.message
        || error?.data?.message
        || error?.error?.message
        || error?.message
        || t('app.updateFailed')
        || 'Update failed';
      // onClose();
      Swal.fire({
        title: errorMessage,
        icon: 'error',
        confirmButtonColor: '#4FA893',
        confirmButtonText: t('app.yes'), // ✅ แก้ไข
        customClass: {
          container: 'container-class',
        },
        willOpen: () => {
          document.querySelector('.swal2-container').style.zIndex = '2000'; // ปรับค่า z-index ที่ต้องการ
        },
      });
    }

    // console.log({
    //   ยอด: newspendAmount || 0,
    //   status: newStatus,
    //   detail: newdetailStatus || '',
    //   remark: newRemark || '',
    //   balance: value.balance,
    //   editBy: userName,
    // });
  };

  // useEffect(() => {}, [newStatus, newdetailStatus, newRemark]);
  useEffect(() => {
    // Discard unsaved edits whenever the dialog is reopened.
    if (open && valueProp) {
      const resultFilterStatus = statusItems.filter(
        (item) => item.label === valueProp.status,
      );
      setNewStatus(resultFilterStatus[0]?.label);

      const resultFilterDetailStatus = detailStatusItems.filter(
        (item) => (item.value || item.label) === valueProp.detail_status,
      );

      setNewDetailStatus(
        resultFilterDetailStatus[0]?.value || resultFilterDetailStatus[0]?.label,
      );
      setnewRemark(valueProp.remark);
      setnewSpendAmount(valueProp.new_spend_amount || valueProp.spend_amount || 0);
      // console.log(resultFilterDetailStatus[0]?.label);
    }

    // setNewDetailStatus(valueProp.detail_status);
  }, [open, valueProp, statusItems, detailStatusItems]);

  // console.log('newRemark', newRemark);
  // console.log('newdetailStatus', newdetailStatus);
  // console.log('valueProp', valueProp);
  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
          maxHeight: 'auto',
          borderRadius: 4,
        },
      }}
      maxWidth="xs"
      open={open}
      {...other}
    >
      <DialogTitle>
        <div className="flex-col mt-2">
          <div className=" text-[#3FC37C] text-xl text-center">
            {t('app.statementRef')} : {valueProp.statement_ref} {/* ✅ แก้ไข */}
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="px-4">
          <div className="text-sm">
            {t('app.effectiveStartDate')} : {/* ✅ แก้ไข */}
            {formatStatementDateTime(valueProp.effective_date)}
          </div>
          <div className="text-sm mt-2">
            {t('app.balance')} : {addCommas(currentBalance)} {/* ✅ แก้ไข */}
          </div>
          <div className="text-sm mt-2">
            {t('app.description')} : {valueProp.description} {/* ✅ แก้ไข */}
          </div>
          <div className="mt-4">
            <TextField
              id="standard-basic"
              label={t('app.amountUsed')} // ✅ แก้ไข
              variant="standard"
              type="number"
              value={newspendAmount ?? ''}
              onChange={(event) => {
                const nextSpendAmount = event.target.value;
                const nextBalance = statementAmount - toNumber(nextSpendAmount);
                setnewSpendAmount(nextSpendAmount);

                if (toNumber(nextSpendAmount) <= 0) {
                  setNewStatus(STATUS_UNMATCH);
                } else if (nextBalance <= 0) {
                  setNewStatus(STATUS_MATCH);
                } else {
                  setNewStatus(STATUS_PARTIAL);
                  if (
                    detailStatusItems?.some(
                      (option) => option.label === DETAIL_PARTIAL_SUSPENSE,
                    )
                  ) {
                    setNewDetailStatus(DETAIL_PARTIAL_SUSPENSE);
                  }
                }
              }}
              sx={{ width: '100%' }}
            />
          </div>

          <div className="mt-4">
            <InputLabel id="status-label">{t('app.status')}</InputLabel>{' '}
            {/* ✅ แก้ไข */}
            <FormControl variant="standard" size="small" sx={{ width: '100%' }}>
              <Select
                labelId="status-label" // ✅ แก้ไข
                id="status"
                value={newStatus || ''}
                label={t('app.status')} // ✅ แก้ไข
                onChange={(event) => {
                  setNewStatus(event.target.value);
                }}
              >
                {statusItems?.map((option, index) => (
                  <MenuItem key={index} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="mt-4">
            <InputLabel id="detail-status-label">
              {t('app.detailStatus')}
            </InputLabel>{' '}
            {/* ✅ แก้ไข */}
            <FormControl variant="standard" size="small" sx={{ width: '100%' }}>
              <Select
                labelId="detail-status-label" // ✅ แก้ไข
                id="detailstatus"
                value={newdetailStatus || ''}
                label={t('app.detailStatus')} // ✅ แก้ไข
                onChange={(event) => {
                  console.log('event.target.value', event.target.value);
                  setNewDetailStatus(event.target.value);
                }}
              >
                {detailStatusItems?.map((option, index) => (
                  <MenuItem key={index} value={option.value || option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="mt-4">
            <InputLabel htmlFor="remark">{t('app.remark')}</InputLabel>
            <textarea
              id="remark"
              value={newRemark || ''}
              onChange={(event) => {
                setnewRemark(event.target.value);
              }}
              rows={4}
              style={{
                width: '100%',
                minHeight: '96px',
                maxHeight: '360px',
                resize: 'vertical',
                overflow: 'auto',
                border: 'none',
                borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                outline: 'none',
                font: 'inherit',
                lineHeight: 1.5,
                padding: '4px 0',
                background: 'transparent',
              }}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="error">
          {t('app.cancel')} {/* ✅ แก้ไข */}
        </Button>
        <Button onClick={handleOk}>{t('app.save')}</Button> {/* ✅ แก้ไข */}
      </DialogActions>
    </Dialog>
  );
}

StatementOnlineEditDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.object.isRequired,
};
